import { createSlice } from "@reduxjs/toolkit";
import type { QueueItemDB } from "@/services/IndexedDBManager";
import type { RootState } from "@/store/store";
import {
  addQueueItemThunk,
  deleteQueueItemThunk,
  initQueueThunk,
  processQueueThunk,
  retryQueueItemThunk,
} from "./ticketQueueOperations";

export const MAX_QUEUE_RETRIES = 3;

/**
 * Queue Item Structure (Redux State)
 */
export type QueueItem<T = Record<string, any>> = QueueItemDB<T>;

// State interface
interface QueueState<T = Record<string, any>> {
  items: QueueItem<T>[];
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: QueueState = {
  items: [],
  loading: false,
  error: null,
};

// Queue Slice
const queueSlice = createSlice({
  name: "queue",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // initQueue
      .addCase(initQueueThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initQueueThunk.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(initQueueThunk.rejected, (state, action) => {
        state.error = action.error.message || "Failed to initialize queue";
        state.loading = false;
      })

      // addToQueue
      .addCase(addQueueItemThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addQueueItemThunk.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(addQueueItemThunk.rejected, (state, action) => {
        state.error = action.error.message || "Failed to add item to queue";
        state.loading = false;
      })

      // processQueue
      .addCase(processQueueThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processQueueThunk.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(processQueueThunk.rejected, (state, action) => {
        state.error = action.error.message || "Failed to process queue";
        state.loading = false;
      })

      // retryQueueItem
      .addCase(retryQueueItemThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(retryQueueItemThunk.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(retryQueueItemThunk.rejected, (state, action) => {
        state.error = action.error.message || "Failed to retry queue item";
        state.loading = false;
      })

      // deleteQueueItem
      .addCase(deleteQueueItemThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteQueueItemThunk.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(deleteQueueItemThunk.rejected, (state, action) => {
        state.error = action.error.message || "Failed to delete queue item";
        state.loading = false;
      });
  },
});

// Selectors (properly typed with RootState)
export const selectAllItems = (state: RootState) => state.tickets.queue.items;
const _selectPendingItems = (state: RootState) =>
  state.tickets.queue.items.filter((i) => i.status === "pending");
const _selectQueueSize = (state: RootState) => state.tickets.queue.items.length;
export const selectIsLoading = (state: RootState) =>
  state.tickets.queue.loading;
const _selectError = (state: RootState) => state.tickets.queue.error;

// Export reducer
export const reducer = queueSlice.reducer;
